import { dailyAnalysisClient } from "./dailyAnalysisClient.ts";
import { HOUSING_TAPE_CONTRACT } from "./housingHtsRegistry.ts";
import type { HousingMarketTick, HousingRtmsChartScopeId } from "./housingTechnicalChart.ts";

export interface HousingTapeRow extends HousingMarketTick {
  lawdCd: string;
}

export interface HousingTapeResult {
  schemaVersion: "frem.housing-tape.v1";
  generationId: string;
  sourceGenerationId: string;
  scope: HousingRtmsChartScopeId;
  from: string;
  to: string;
  band: string;
  rows: HousingTapeRow[];
  totalMatchedRows: number;
  truncated: boolean;
  sourceRevisionPinned: true;
  cachePolicy: "NO_STORE";
  readTelemetry: { requests: number; bytes: number; partitions: number };
}

export class HousingTapeClientError extends Error {
  code: string;
  status: number;

  constructor(code: string, status: number, message = code) {
    super(message);
    this.name = "HousingTapeClientError";
    this.code = code;
    this.status = status;
  }
}

type GenerationPin = { generationId: string; analysisAsOf: string; computedAt: string };
type PinClient = { index(): Promise<GenerationPin> };

function object(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new HousingTapeClientError("tape_invalid_response", 502, label);
  return value as Record<string, unknown>;
}

function finite(value: unknown, label: string) {
  const number = Number(value);
  if (!Number.isFinite(number)) throw new HousingTapeClientError("tape_invalid_response", 502, label);
  return number;
}

function monthWindow(month: string) {
  if (!/^\d{4}-\d{2}$/.test(month)) throw new HousingTapeClientError("invalid_tape_month", 400);
  const [year, monthNumber] = month.split("-").map(Number);
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  if (!Number.isInteger(year) || monthNumber < 1 || monthNumber > 12 || lastDay < 28) throw new HousingTapeClientError("invalid_tape_month", 400);
  return { from: `${month}-01`, to: `${month}-${String(lastDay).padStart(2, "0")}` };
}

function parseRow(value: unknown): HousingTapeRow {
  const row = object(value, "invalid_tape_row");
  const date = String(row.date ?? "");
  const lawdCd = String(row.lawdCd ?? "");
  const sigungu = String(row.sigungu ?? "");
  const legalDong = String(row.legalDong ?? "");
  const complexName = String(row.complexName ?? "");
  const bandId = String(row.bandId ?? "");
  const exclusiveAreaM2 = finite(row.exclusiveAreaM2, "invalid_tape_area");
  const priceKrw = finite(row.priceKrw, "invalid_tape_price");
  const floor = row.floor == null ? null : finite(row.floor, "invalid_tape_floor");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{5}$/.test(lawdCd) || !sigungu || !legalDong || !complexName || !bandId || exclusiveAreaM2 <= 0 || priceKrw <= 0 || (floor != null && !Number.isInteger(floor))) {
    throw new HousingTapeClientError("tape_invalid_response", 502, "invalid_tape_row");
  }
  return { date, lawdCd, sigungu, legalDong, complexName, exclusiveAreaM2, floor, priceKrw, bandId };
}

function requestSignal(signal?: AbortSignal) {
  const timeout = AbortSignal.timeout(30_000);
  return signal ? AbortSignal.any([signal, timeout]) : timeout;
}

function validateResult(raw: unknown, pin: GenerationPin, scope: HousingRtmsChartScopeId, band: string, from: string, to: string): HousingTapeResult {
  const body = object(raw, "invalid_tape_body");
  if (body.schemaVersion !== "frem.housing-tape.v1" || body.generationId !== pin.generationId || body.scope !== scope || body.from !== from || body.to !== to || body.band !== band || body.sourceRevisionPinned !== true || body.cachePolicy !== "NO_STORE") {
    throw new HousingTapeClientError("tape_generation_contract_mismatch", 409);
  }
  const rows = Array.isArray(body.rows) ? body.rows.map(parseRow) : null;
  if (!rows) throw new HousingTapeClientError("tape_invalid_response", 502);
  const totalMatchedRows = Number(body.totalMatchedRows);
  const truncated = body.truncated;
  const telemetry = object(body.readTelemetry, "invalid_tape_telemetry");
  const requests = Number(telemetry.requests), bytes = Number(telemetry.bytes), partitions = Number(telemetry.partitions);
  if (!Number.isInteger(totalMatchedRows) || totalMatchedRows < rows.length || rows.length > HOUSING_TAPE_CONTRACT.maxRows || typeof truncated !== "boolean" ||
      !Number.isInteger(requests) || requests < 0 || requests > HOUSING_TAPE_CONTRACT.maxRequests ||
      !Number.isInteger(bytes) || bytes < 0 || bytes > HOUSING_TAPE_CONTRACT.maxAggregateBytes ||
      !Number.isInteger(partitions) || partitions < 0 || partitions > HOUSING_TAPE_CONTRACT.maxPartitions) {
    throw new HousingTapeClientError("tape_invalid_response", 502);
  }
  return {
    schemaVersion: "frem.housing-tape.v1",
    generationId: pin.generationId,
    sourceGenerationId: String(body.sourceGenerationId ?? ""),
    scope,
    from,
    to,
    band,
    rows,
    totalMatchedRows,
    truncated,
    sourceRevisionPinned: true,
    cachePolicy: "NO_STORE",
    readTelemetry: { requests, bytes, partitions }
  };
}

export function createHousingTapeClient(request: typeof fetch = (...args) => fetch(...args), pinClient: PinClient = dailyAnalysisClient) {
  return {
    async read(input: { scope: HousingRtmsChartScopeId; month: string; band: string }, signal?: AbortSignal): Promise<HousingTapeResult> {
      signal?.throwIfAborted();
      const pin = await pinClient.index();
      signal?.throwIfAborted();
      const { from, to } = monthWindow(input.month);
      const query = new URLSearchParams({ generation: pin.generationId, scope: input.scope, from, to, band: input.band });
      const response = await request(`/api/housing-tape?${query}`, { cache: "no-store", redirect: "error", signal: requestSignal(signal) });
      let raw: unknown = null;
      try { raw = await response.json(); } catch { /* handled below */ }
      if (!response.ok) {
        const code = raw && typeof raw === "object" && !Array.isArray(raw) ? String((raw as Record<string, unknown>).status ?? "tape_unavailable") : "tape_unavailable";
        throw new HousingTapeClientError(code, response.status);
      }
      return validateResult(raw, pin, input.scope, input.band, from, to);
    }
  };
}

export function housingTapeErrorMessage(error: unknown) {
  const code = error instanceof HousingTapeClientError ? error.code : "tape_unavailable";
  if (code === "tape_archive_source_not_addressable") return "이 월은 검증된 이관 아카이브 기반이라 현재 generation-pinned 개별 거래 조회를 제공하지 않습니다. 집계 차트는 사용할 수 있지만 실거래 Tape는 N/A입니다.";
  if (code === "tape_month_not_pinned_for_scope") return "선택한 분석 세대에서 이 지역·월이 완전한 RTMS 입력으로 고정되지 않아 Tape를 표시하지 않습니다.";
  if (["tape_pinned_artifact_unavailable", "tape_pinned_artifact_missing"].includes(code)) return "선택한 분석 세대가 고정한 RTMS artifact를 SDP에서 정확히 다시 읽을 수 없어 Tape를 표시하지 않습니다.";
  if (code === "tape_read_budget_exceeded") return "안전 조회 한도를 초과해 실거래 Tape 조회를 중단했습니다.";
  if (code === "generation_not_published") return "현재 페이지가 고정한 일일 분석 세대를 찾을 수 없어 Tape를 표시하지 않습니다.";
  if (code === "tape_scope_unavailable") return "이 지역은 아직 bounded on-demand Tape 대상이 아닙니다.";
  if (code === "tape_generation_contract_mismatch") return "응답 세대가 현재 페이지의 고정 세대와 일치하지 않아 Tape를 폐기했습니다.";
  return "실거래 Tape를 안전하게 읽지 못했습니다. 기존 집계 차트는 그대로 사용할 수 있습니다.";
}

export const housingTapeClient = createHousingTapeClient();
